/*
 * Copyright (c) 2017 teamapps.org (see code comments for author's name)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

///<reference types="swfobject"/>

import * as $ from "jquery";
import {LiveStreamPlayer} from "./LiveStreamPlayer";
import {UiComponent} from "../UiComponent";
import {DeferredExecutor} from "../util/DeferredExecutor";
import {TeamAppsUiContext} from "../TeamAppsUiContext";
import {generateUUID, loadScriptAsynchronously} from "../Common";
import {
	UiRtmpLiveStreamPlayer_ResultOfGetFlashPlayerVersionEvent, UiRtmpLiveStreamPlayerCommandHandler, UiRtmpLiveStreamPlayerConfig,
	UiRtmpLiveStreamPlayerEventSource
} from "../../generated/UiRtmpLiveStreamPlayerConfig";
import {TeamAppsEvent} from "../util/TeamAppsEvent";
import {EventFactory} from "../../generated/EventFactory";
import {UiMediaInputSettingsConfig} from "../../generated/UiMediaInputSettingsConfig";

export enum RtmpPlayerAccessState {
		ACCESS_NOT_YET_REQUESTED,
		ACCESS_BEING_REQUESTED,
		ACCESS_GRANTED,
		ACCESS_DENIED
	}

	export interface UiRtmpLiveStreamPlayerListener {
		onFlashReady: (player: UiRtmpLiveStreamPlayer) => void,
		onInputDeviceAccessChange: (player: UiRtmpLiveStreamPlayer, microphoneAccessGranted: boolean, cameraAccessGranted: boolean, explicitelyRequested: boolean) => void
	}

	export class UiRtmpLiveStreamPlayer extends UiComponent<UiRtmpLiveStreamPlayerConfig> implements LiveStreamPlayer, UiRtmpLiveStreamPlayerCommandHandler, UiRtmpLiveStreamPlayerEventSource {

		public readonly onResultOfGetFlashPlayerVersion: TeamAppsEvent<UiRtmpLiveStreamPlayer_ResultOfGetFlashPlayerVersionEvent> = new TeamAppsEvent<UiRtmpLiveStreamPlayer_ResultOfGetFlashPlayerVersionEvent>(this);
		
		public static readonly flashScriptLoadedDeferedExecutor: DeferredExecutor = new DeferredExecutor();
		public static readonly REQUIRED_FLASH_VERSION = "21.0.0";

		private playing: boolean = false;
		private publishing: boolean = false;
		private bufferedLiveStreamPlayerCommands: Function[] = [];

		public listener: UiRtmpLiveStreamPlayerListener;

		private $main: JQuery;

		private player: HTMLObjectElement & {
			isReady(): boolean;
			stopVideo(): void;
			playDynamicVideo(url: string, streamDescriptors: { name: string, bitrate: number }[]): void;
			publish(serverUrl: string, streamName: string, audioKiloBitsPerSecond: number, video: boolean, videoWidth: number, videoHeight: number, videoFps: number, videoKiloBitsPerSecond: number): void;
			unPublish(): void;
			setVolume(volume: number): void;
			requestInputDeviceAccess(mic: boolean, cam: boolean): void;
			getAccessState(): RtmpPlayerAccessState;
		};

		constructor(config: UiRtmpLiveStreamPlayerConfig, context: TeamAppsUiContext) {
			super(config, context);
			UiRtmpLiveStreamPlayer.loadSwfObjectScriptIfNotYetLoaded();
			this.$main = $('<div class="UiRtmpLiveStreamPlayer"></div>');

			(window as any).teamappsContextReferenceForUiRtmpLiveStreamPlayerCallback = context;
		}

		private static loadSwfObjectScriptIfNotYetLoaded() {
			if (!UiRtmpLiveStreamPlayer.flashScriptLoadedDeferedExecutor.ready) {
				loadScriptAsynchronously("/static/flash/swfobject.min.js", () => {
					UiRtmpLiveStreamPlayer.flashScriptLoadedDeferedExecutor.ready = true;
				});
			}
		}

		public getFlashPlayerVersion() {
			UiRtmpLiveStreamPlayer.getFlashPlayerVersionPromise().then((versionString) => {
				this.onResultOfGetFlashPlayerVersion.fire(EventFactory.createUiRtmpLiveStreamPlayer_ResultOfGetFlashPlayerVersionEvent(
					this.getId(),
					versionString,
					UiRtmpLiveStreamPlayer.REQUIRED_FLASH_VERSION,
					swfobject.hasFlashPlayerVersion(UiRtmpLiveStreamPlayer.REQUIRED_FLASH_VERSION))
				)
			});
		}

		public static getFlashPlayerVersionPromise(): Promise<string> {
			UiRtmpLiveStreamPlayer.loadSwfObjectScriptIfNotYetLoaded();
			return UiRtmpLiveStreamPlayer.flashScriptLoadedDeferedExecutor.invokeWhenReady(() => {
				let hasFlashPlayer = swfobject.hasFlashPlayerVersion("1.0.0");

				let versionString: string = null;
				if (hasFlashPlayer) {
					let playerVersion = swfobject.getFlashPlayerVersion();
					versionString = playerVersion.major + '.' + playerVersion.minor + '.' + playerVersion.release;
					return versionString;
				}
			});
		};

		play(url: string, streamName?: string) {
			this.playing = true;
			this.invokeWhenLiveStreamPlayerReady(() => {
				this.player.playDynamicVideo(url, [
					{
						name: streamName,
						bitrate: 100000
					}
				])
			});
		}

		stop() {
			this.playing = false;
			this.invokeWhenLiveStreamPlayerReady(() => this.player.stopVideo());
		}

		setVolume(volume: number): void {
			this.invokeWhenLiveStreamPlayerReady(() => this.player.setVolume(volume))
		}

		private initFlashPlayer(targetElement: Element | JQuery) {
			let targetDivId = generateUUID();
			$(`<div id="${targetDivId}">`).appendTo(targetElement);

			var swfVersionStr = "21";
			var xiSwfUrlStr = "flash/playerProductInstall.swf";
			var flashvars: any = {};
			flashvars.teamappsComponentId = this.getId();
			var params: any = {};
			params.quality = "high";
			params.bgcolor = "#eee"; // TODO
			params.allowscriptaccess = "sameDomain";
			params.allowfullscreen = "true";
			params.wmode = "transparent";
			var attributes = {
				id: generateUUID(),
				styleclass: "rtmp-player",
				name: "player",
				align: "middle",
				backgroundColor: "#FCF9EB" // TODO
			};

			UiRtmpLiveStreamPlayer.flashScriptLoadedDeferedExecutor.invokeWhenReady(() => {
				swfobject.embedSWF(
					"/static/flash/livestream/teamapps-livestream-flash.swf",
					targetDivId,
					"100%", "100%",
					swfVersionStr, xiSwfUrlStr,
					flashvars, params, attributes);
				this.player = <any> $(targetElement).find("object")[0];
			});
		}

		private invokeWhenLiveStreamPlayerReady(fn: Function) {
			if (this.isFlashPlayerReady()) {
				fn.call(this);
			} else {
				this.bufferedLiveStreamPlayerCommands.push(fn);
			}
		}

		public onReady() {
			for (var i = 0; i < this.bufferedLiveStreamPlayerCommands.length; i++) {
				this.bufferedLiveStreamPlayerCommands[i].call(this);
			}
			this.bufferedLiveStreamPlayerCommands.length = 0;

			this.listener && this.listener.onFlashReady(this);
		}

		private isFlashPlayerReady(): boolean {
			if (!this.player) {
				return false;
			}
			try {
				return this.player.isReady();
			} catch (e) {
				return false;
			}
		}

		public publish(rtmpServerUrl: string, streamName: string, inputSettings: UiMediaInputSettingsConfig) {
			this.publishing = true;
			this.invokeWhenLiveStreamPlayerReady(() => this.player.publish(
				rtmpServerUrl,
				streamName,
				inputSettings.audioKiloBitsPerSecond,
				inputSettings.video,
				inputSettings.videoWidth,
				inputSettings.videoHeight,
				inputSettings.videoFps,
				inputSettings.videoKiloBitsPerSecond
			));
		}

		public unpublish() {
			this.publishing = false;
			this.invokeWhenLiveStreamPlayerReady(() => this.player.unPublish());
		}

		public isPublishing(): boolean {
			return this.publishing;
		}

		public isPlaying(): boolean {
			return this.playing;
		}

		getMainDomElement(): JQuery {
			return this.$main;
		}

		public requestInputDeviceAccess(microphone: boolean, camera: boolean) {
			this.invokeWhenLiveStreamPlayerReady(() => {
				this.player.requestInputDeviceAccess(microphone, camera);
			});
		}

		public getAccessState(): RtmpPlayerAccessState {
			if (!this.isFlashPlayerReady()) {
				return RtmpPlayerAccessState.ACCESS_NOT_YET_REQUESTED;
			} else {
				return this.player.getAccessState();
			}
		}

		protected onAttachedToDom(): void {
			this.initFlashPlayer(this.$main);
		}

		public destroy(): void {
			// nothing to do
		}

		onInputDeviceAccessChange(microphoneAccessGranted: boolean, cameraAccessGranted: boolean, explicitelyRequested: boolean) {
			this.listener && this.listener.onInputDeviceAccessChange(this, microphoneAccessGranted, cameraAccessGranted, explicitelyRequested);
		}
	}

	export function UiLiveStreamCommponent_rtmpPlayerReadyCallback(teamappsComponentId:string) {
		let player = <UiRtmpLiveStreamPlayer> ((window as any).teamappsContextReferenceForUiRtmpLiveStreamPlayerCallback as TeamAppsUiContext).getComponentById(teamappsComponentId);
		player.onReady();
	}

	export function UiLiveStreamCommponent_onInputDeviceAccessChange(teamappsComponentId:string, microphoneAccessGranted:boolean, cameraAccessGranted:boolean, explicitelyRequested:boolean) {
		//this.logger.debug("####### UiLiveStreamCommponent_onInputDeviceAccessChange: " + teamappsComponentId);
		let player = <UiRtmpLiveStreamPlayer> ((window as any).teamappsContextReferenceForUiRtmpLiveStreamPlayerCallback as TeamAppsUiContext).getComponentById(teamappsComponentId);
		player.onInputDeviceAccessChange(microphoneAccessGranted, cameraAccessGranted, explicitelyRequested);
	}
